import { formatValue } from "../utils/helpers";
import metricMetadataIcon from '../assets/metric-metadata-icon-slate.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata, indexMetricIds } from "../utils/dashboardUtils";
import { getIconByName } from "../utils/icons";

interface DoubleMetricCardProps {
    metricOne: DisaggregatedMetric;
    metricTwo: DisaggregatedMetric;
    subtextOne: string;
    subtextTwo: string;
    title: string;
    metadataId: number;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const DoubleMetricCard = ({ metricOne, metricTwo, subtextOne, subtextTwo, title, metadataId, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: DoubleMetricCardProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    // Destructure metricOne properties
    const { versions: versions1 } = metricOne;
    const { value: value1, unit: unit1, percent_calc: percentCalc1 } = versions1[0];

    // Calculate formatted value for metricOne
    const formattedValue1 = unit1 === "ratio" ? `${percentCalc1?.n}/${percentCalc1?.d}` : value1;

    // Destructure metricTwo properties
    const { versions: versions2 } = metricTwo;
    const { value: value2, unit: unit2, percent_calc: percentCalc2 } = versions2[0];

    // Calculate formatted value for metricTwo
    const formattedValue2 = unit2 === "ratio" ? `${percentCalc2?.n}/${percentCalc2?.d}` : value2;

    const parentMetric = disaggData?.find(m => m.id === metadataId);
    const parentName = parentMetric?.name;

    const unitDescriptor = parentMetric ? parentMetric.versions[0]?.unit_descriptor : '';

    // Index metric?
    const isParentIndexMetric = parentMetric ? indexMetricIds.includes(parentMetric.id) : false;
    const isIndexMetricOne = indexMetricIds.includes(metricOne.id);
        const isIndexMetricTwo = indexMetricIds.includes(metricTwo.id);


    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${metadataId ?? 'main'}`;
    const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
            // Return ongoing request if already in-flight
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    // Prefetch on mount (throttled sequentially)
    useEffect(() => {
        // Only fetch if not already cached or loaded
        if (!metadata) {
            fetchWithCache(`metadata-${metadataId}`, () => fetchMetricMetadata(metadataId))
            .then(res => res?.data && setMetadata(res.data))
            .catch(console.error);
        }

        if (!distributionData) {
            const distKey = `distData-${metadataId}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`;
            fetchWithCache(distKey, () => fetchIndicatorBinCount(metadataId, geoDetails, geoTag))
            .then(res => res && setDistributionData(res))
            .catch(console.error);
        }

        if (!countyDistributionData) {
            const countyKey = `countyDist-${metadataId}-53033`;
            fetchWithCache(countyKey, () => fetchDistributionData(metadataId, [], null))
            .then(res => res && setCountyDistributionData(res))
            .catch(console.error);
        }
    }, [metadataId, geoDetails, geoTag]);

    const handleClick = async () => {
        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
            setShowMetadata((prev) => !prev);
            setLoading(true);

            try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                metadata
                ? Promise.resolve({ data: metadata })
                : fetchWithCache(`metadata-${metadataId}`, () => fetchMetricMetadata(metadataId)),

                distributionData
                ? Promise.resolve(distributionData)
                : fetchWithCache(`distData-${metadataId}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`, () =>
                    fetchIndicatorBinCount(metadataId, geoDetails, geoTag)
                    ),

                countyDistributionData
                ? Promise.resolve(countyDistributionData)
                : fetchWithCache(`countyDist-${metadataId}-53033`, () =>
                    fetchDistributionData(metadataId, [], null)
                    ),
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

            } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`));
            } finally {
            setLoading(false);
            }
        }, 150);
    };

    return (
        <div 
            className="relative overflow-hidden group hover:shadow-lg transition-all border rounded-xl bg-white" 
            style={{ 
                borderLeftWidth: isParentIndexMetric ? "4px" : undefined, borderLeftColor: isParentIndexMetric ? "#3B5C67" : undefined,
                }}
        >
            <div className="p-3">
                <div className="flex items-start justify-between mb-1 gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                        {/* Metric Icon */}
                        <div className="flex-shrink-0 rounded-lg bg-gresham-slate/10 p-2 group-hover:bg-gresham-slate/20 transition-colors">
                            <img
                                src={svg ? svg : getIconByName(parentName || title)}
                                alt={`${title} icon`}
                                className="w-8 h-8 text-blue-500"
                            />
                        </div>

                        {/* Metric Title and Description */}
                        <div className="space-y-0.5">
                            <h3 className="text-sm font-semibold text-port-text">
                                {title}
                            </h3>

                            {unitDescriptor && (
                                <p className="text-xs text-port-text leading-tight">
                                    {unitDescriptor}
                                </p>
                            )}
                            {isParentIndexMetric &&
                                <div className="rounded-lg bg-gresham-slate h-4 w-10 px-1.5 text-[10px] text-white text-center">Index</div> 
                            }
                        </div>

                        {/* Metric Values */}
                        <div className="flex items-center gap-5 min-w-0">
                            {/* First Metric */}
                            <div className="flex flex-col min-w-0">
                                <span className="text-xs font-semibold text-port-text">
                                    {subtextOne}
                                </span>
                                <span className="text-[1.75rem] font-semibold leading-none whitespace-nowrap">
                                    {formatValue(formattedValue1, unit1, metricOne.id)}
                                </span>
                                {isIndexMetricOne &&
                                    <div className="rounded-lg bg-gresham-slate h-4 w-10 px-1.5 text-[10px] text-white text-center">Index</div> 
                                }
                            </div>

                            {/* Second Metric */}
                            <div className="flex flex-col min-w-0">
                                <span className="text-xs font-semibold text-port-text">
                                    {subtextTwo}
                                </span>
                                <span className="text-[1.75rem] font-semibold leading-none whitespace-nowrap">
                                    {formatValue(formattedValue2, unit2, metricTwo.id)}
                                </span>
                                {isIndexMetricTwo &&
                                    <div className="rounded-lg bg-gresham-slate h-4 w-10 px-1.5 text-[10px] text-white text-center">Index</div> 
                                }
                            </div>
                        </div>
                    </div>

                    {/* Metadata Icon */}
                    {showMetadataIcon && (
                        <button
                            ref={triggerRef}
                            type="button"
                            onClick={handleClick}
                            aria-haspopup="dialog"
                            aria-expanded={showMetadata}
                            aria-controls={modalId}
                            aria-label={`Additional information about ${title}`}
                            className="
                                inline-flex h-6 w-6 flex-shrink-0
                                items-start justify-center rounded
                                transition-transform hover:scale-105 focus:scale-105
                                focus-visible:outline-none
                                focus-visible:ring-2 focus-visible:ring-offset-2
                                focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                                dark:focus-visible:ring-white
                                dark:focus-visible:ring-offset-neutral-900
                            "
                        >
                            <img
                                src={metricMetadataIcon}
                                alt=""
                                aria-hidden="true"
                                className="h-3 w-3"
                                draggable="false"
                            />
                        </button>
                    )}
                </div>
            </div>

            {/* Bottom Gradient Hover Accent */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />

            {/* Metadata Modal */}
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={metadataId}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                geoTag={geoTag}
                svg={svg}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};


export default DoubleMetricCard;
